import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";

import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandWordmark from "@/components/BrandWordmark";
import { ADMIN_PATH } from "@/lib/adminRoutes";
import { detectBrowserLanguage, detectLanguageByIp } from "@/lib/localeDetection";
import LanguageSwitcher from "@/components/auth/LanguageSwitcher";

const HERO_IMG =
  "https://images.unsplash.com/photo-1649615644622-6d83f48e69c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB3ZWRkaW5nJTIwdGFibGUlMjBzZXR1cHxlbnwwfHx8fDE3ODAwNDkzODV8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, pianoScaduto } = useAuth();
  const { t, language, languageSource, setAutoLanguage } = useTheme();
  const en = language === "en";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  // Data leggibile della fine dell'accesso. Se manca o e' illeggibile il
  // messaggio resta valido, solo senza data: meglio che una data sbagliata.
  const scadenza = (() => {
    const iso = pianoScaduto?.scadutoIl;
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(en ? "en-GB" : "it-IT", {
      day: "numeric", month: "long", year: "numeric",
    });
  })();
  const location = useLocation();

  // Browser language wins when it is Italian or English. For other browser
  // languages, use a best-effort IP country lookup without delaying login.
  useEffect(() => {
    if (languageSource === "manual" || detectBrowserLanguage()) return undefined;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    detectLanguageByIp({ signal: controller?.signal }).then((detected) => {
      if (detected) setAutoLanguage(detected);
    });
    return () => controller?.abort();
  }, [languageSource, setAutoLanguage]);

  // Intended URL: where the user was trying to go before being sent to login
  const intendedFrom = location.state?.from;

  const resolveRedirect = (loggedInUser) => {
    if (loggedInUser?.role === "admin" || loggedInUser?.is_admin) return ADMIN_PATH;
    // I collaboratori non hanno dashboard/clienti: landing su Eventi.
    if (loggedInUser?.is_collaborator || loggedInUser?.role === "collaborator") return "/events";
    // Restore intended URL for regular users, but never send them to admin path
    if (intendedFrom && intendedFrom !== ADMIN_PATH) return intendedFrom;
    return "/dashboard";
  };

  // Already authenticated: skip login and go to correct destination
  if (user) return <Navigate to={resolveRedirect(user)} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      toast.error(t("auth.fill_credentials"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error(t("auth.invalid_email"));
      return;
    }
    setSubmitting(true);
    try {
      const loggedInUser = await login(trimmedEmail, password);
      toast.success(t("auth.welcome_back"));
      navigate(resolveRedirect(loggedInUser), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO_IMG} alt="Luxury wedding table" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/30 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-primary-foreground">
          <BrandWordmark size="md" variant="light" />
          <div className="space-y-2 max-w-md">
            <div className="label-eyebrow text-primary-foreground/70">Aurea Pro · CRM</div>
            <h1 className="serif text-4xl lg:text-5xl font-light leading-[1.1]">
              {t("auth.login_subtitle").split(".")[0]}.
            </h1>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-8 lg:p-16">
        <div className="absolute right-6 top-6 z-10 lg:right-10 lg:top-10">
          <LanguageSwitcher />
        </div>
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-8" data-testid="login-form" noValidate>
          <div className="space-y-3">
            <BrandWordmark size="lg" data-testid="login-brand-wordmark" />
            <div className="label-eyebrow">{t("auth.welcome_back")}</div>
            <h2 className="serif text-4xl font-light tracking-tight">{t("auth.login_title")}</h2>
            <p className="text-sm text-muted-foreground">{t("auth.login_subtitle")}</p>
          </div>

          {/* Chi rientra con il piano finito arriva qui, non su una pagina
              che non ha chiesto: gli si dice cosa e' successo e che i suoi
              dati sono al loro posto. */}
          {pianoScaduto && (
            <div
              className="rounded-sm border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
              data-testid="login-piano-scaduto"
            >
              {en
                ? `Your plan ended${scadenza ? ` on ${scadenza}` : ""}. Sign in as usual to reactivate it: your account and your data are still here.`
                : `Il tuo piano è scaduto${scadenza ? ` il ${scadenza}` : ""}. Accedi come sempre per riattivarlo: l'account e i tuoi dati sono al loro posto.`}
            </div>
          )}

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-eyebrow">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-eyebrow">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground tracking-wider uppercase text-xs"
              data-testid="login-submit-button"
            >
              {submitting ? t("auth.logging_in") : t("auth.login")}
            </Button>
            <div className="text-center">
              <Link
                to="/password-dimenticata"
                className="text-sm text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
                data-testid="link-forgot-password"
              >
                {t("auth.forgot_password")}
              </Link>
            </div>
          </div>

          <div className="text-sm text-muted-foreground text-center">
            {t("auth.no_account")}{" "}
            <Link to="/register" className="text-primary underline-offset-4 hover:underline" data-testid="link-register">
              {t("auth.create_account")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
